import {
  ArtifactDigest,
  ArtifactFact,
  ArtifactReference,
} from './artifact-digest-types';
import { ProviderMention } from './treatment-types';

/**
 * Sources of audio conversations
 */
export enum ConversationSource {
  TWILIO = 'twilio',
  DIALPAD = 'dialpad',
  BLAND_AI = 'bland_ai',
  RINGCENTRAL = 'ringcentral',
}
/**
 * Represents an audio conversation in the system with call metadata.
 * Maps to the Django AudioConversation model which handles call recordings and metadata.
 */
export interface AudioConversation {
  /** Unique identifier for the audio conversation */
  id: string;
  /** Display name of the audio conversation */
  name: string;
  /** Start time of the call */
  start_timestamp: string;
  /** End time of the call */
  end_timestamp: string;
  /** Duration of the call */
  duration: string;
  /** Whether the call was inbound or outbound */
  is_inbound: boolean;
  /** Attorney's phone number */
  attorney_number: string;
  /** Client's phone number */
  client_number: string;
  /** Twilio phone number used for the call */
  twilio_number: string;
  /** Twilio incoming call SID */
  twilio_sid_incoming_call: string;
  /** Dialpad call ID */
  dialpad_call_id: string;
  /** RingCentral call ID (telephony session ID) */
  ringcentral_call_id: string | null;
  /** Source of the conversation (twilio, dialpad, bland_ai, ringcentral) */
  source: ConversationSource | null;
  /** ISO timestamp of creation */
  created_at: string;
  /** ISO timestamp of last modification */
  modified_at: string;
  /** ID of the associated artifact */
  artifact_id: string | null;
  /** ID of the latest transcript */
  transcript_id: string | null;
  /** ID of the transcript's artifact */
  transcript_artifact_id: string | null;
  /** S3 path to the audio recording */
  audio_path: string | null;
}

/**
 * Detailed audio conversation information including all artifact-related data.
 * Provides comprehensive information about a call including transcript artifact data.
 */
export interface AudioConversationDetail extends AudioConversation {
  /** S3 path to the audio recording */
  audio_path: string | null;
  /** Summary of transcript artifact content */
  artifact_digest?: ArtifactDigest | null;
  /** Facts extracted from the transcript artifact */
  artifact_facts?: ArtifactFact[];
  /** Named entities in the transcript artifact */
  artifact_named_entities?: ProviderMention[];
  /** References in the transcript artifact */
  artifact_references?: ArtifactReference[];
}
